import { ChangeEvent, Fragment, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Grid, Link, TextField, Typography } from '@mui/material'

import { BasicDialog, LoadingFeedback, Select } from 'components'

import { formatters, mixpanel } from 'helpers'

import { useAuth, useSnackbar } from 'hooks'

import { SolicitationContactModalType } from './solicitationContactModal.types'

import schema, {
  SolicitationContactModalControlType,
  SolicitationContactModalSchemaType,
} from './schema'
import constants from 'constants/index'
import service from 'service'

const SolicitationContactModal = ({
  open,
  onClose,
}: SolicitationContactModalType) => {
  const {
    SOLICITATION_CONTACT_CONTROL_ITEMS,
    SEGMENT_OPTIONS,
    EMPLOYEES_OPTIONS,
  } = constants.dponet

  const [loading, setLoading] = useState(false)

  const { user, userCompany } = useAuth()

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<SolicitationContactModalSchemaType>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: user?.name || '',
      email: userCompany?.company?.email || '',
      phone: formatters.phoneDynamicMask(userCompany?.company?.phone) || '',
      document: formatters.cpfOrCnpj(userCompany?.company?.document) || '',
      companyName: userCompany?.company?.name || '',
    },
  })

  const { snackbar } = useSnackbar()

  const handleOnChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: SolicitationContactModalControlType
  ) => {
    if (name === 'phone')
      return formatters.phoneDynamicMask(event?.target?.value)

    if (name === 'document') return formatters.cpfOrCnpj(event?.target?.value)

    return event?.target?.value
  }

  const handleSelectOptions = (stringArray: string[]) => {
    return stringArray?.map((item) => ({
      id: item,
      name: item,
    }))
  }

  const segmentsOptions = handleSelectOptions(SEGMENT_OPTIONS)
  const employeesOptions = handleSelectOptions(EMPLOYEES_OPTIONS)

  const onSubmit = async (data: SolicitationContactModalSchemaType) => {
    try {
      setLoading(true)

      await service.dponet.ticket.contactSpecialist({
        ticket: { ...data },
      })

      snackbar.open({
        message: 'Solicitação de contato enviada com sucesso!',
        variant: 'success',
      })
      mixpanel('RDStation', 'Solicitação de contato', {
        action: 'solicitar_contato_especialista-dponet',
      })
      onClose()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <BasicDialog
      open={open}
      onClose={onClose}
      title="Solicitação de contato"
      disableBackdropClick
      dialogActions={
        <Fragment>
          <Button variant="outlined" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="solicitation-contact-modal-form"
          >
            Solicitar contato
          </Button>
        </Fragment>
      }
      hiddenCloseButton
      maxWidth="md"
    >
      <LoadingFeedback open={loading} />
      <form
        onSubmit={handleSubmit(onSubmit)}
        id="solicitation-contact-modal-form"
      >
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography fontWeight={600} color="primary">
              Adequação a LGPD completa e com banner de cookies ilimitado!
            </Typography>
            <Typography color="primary">
              Fale com um de nossos especialistas.
            </Typography>
          </Grid>
          {SOLICITATION_CONTACT_CONTROL_ITEMS.map((item, index) => (
            <Grid key={index} item xs={12} md={6}>
              <Controller
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={item.label}
                    error={!!errors?.[item.name]}
                    helperText={errors?.[item.name]?.message}
                    required
                    fullWidth
                    type={item?.type || 'text'}
                    onChange={(event) => {
                      const value = handleOnChange(event, item.name)
                      field.onChange(value)
                    }}
                  />
                )}
                control={control}
                name={item.name}
              />
            </Grid>
          ))}
          <Grid item xs={12} md={6}>
            <Controller
              render={({ field }) => (
                <Select
                  {...field}
                  label="Segmento"
                  items={segmentsOptions}
                  error={!!errors?.segment}
                  helperText={errors?.segment?.message}
                  required
                  fullWidth
                />
              )}
              control={control}
              name="segment"
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              render={({ field }) => (
                <Select
                  {...field}
                  label="Quantos colaboradores têm a sua organização?"
                  items={employeesOptions}
                  error={!!errors?.segment}
                  helperText={errors?.segment?.message}
                  required
                  fullWidth
                />
              )}
              control={control}
              name="employees"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="primary">
              Ao informar meus dados, estou ciente das diretrizes da{' '}
              <Link
                fontWeight={600}
                href="https://www.dponet.com.br/politica-de-privacidade-do-dponet/"
                target="_blank"
              >
                Política de Privacidade
              </Link>
              &nbsp;e os&nbsp;
              <Link
                fontWeight={600}
                href="https://www.dponet.com.br/termos-e-condicoes-da-oferta-de-maio-de-2025/"
                target="_blank"
              >
                termos da oferta da DPOnet
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </form>
    </BasicDialog>
  )
}

export default SolicitationContactModal
